import React, { useContext } from 'react';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UserProfile } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';

const styles = {
  container: {
    marginBottom: '16px',
  },
  list: {
    margin: 0,
    paddingLeft: '1.25rem',
  },
};

const bySequence = <T extends { sequence: number }>(items: T[]): T[] =>
  [...items].sort((left, right) => left.sequence - right.sequence);

// Visible to internal users, plus anyone who is an active "Assignee" or
// "Project Manager" on the ticket (Party Involved) -- matches the email/status
// matching convention used by ticketPermissions in ../../permissions.tsx.
const isActiveAssigneeOrProjectManager = (
  ticket: TicketQueryResult,
  userProfile: UserProfile,
): boolean =>
  isNotNull(ticket?.ticket?.partiesInvolved) &&
  ticket.ticket.partiesInvolved.some(
    (party) =>
      party.email.toLowerCase() === userProfile.email.toLowerCase() &&
      party.status === 'ACTIVE' &&
      (party.type === 'Assignee' || party.type === 'Project Manager'),
  );

// Shows the event configuration's technician-facing guidance (likely causes,
// actions to take) for this ticket, when it has one. Gated to internal users
// plus the ticket's active Assignee/Project Manager -- everyone else never
// sees it, regardless of whether the data is populated.
const TechnicianActionsPanel = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);

  const canView =
    permissions.internal.hasPermissions(userProfile) ||
    isActiveAssigneeOrProjectManager(ticket, userProfile);

  if (!canView) {
    return null;
  }

  const technicianInfo = ticket?.ticket?.extras?.technicianInfo;

  const likelyCauses = bySequence(technicianInfo?.likelyCauses ?? []);
  const actions = bySequence(technicianInfo?.actions ?? []);

  const hasContent =
    isNotNull(technicianInfo) &&
    (likelyCauses.length > 0 || actions.length > 0);

  if (!hasContent) {
    return null;
  }

  return (
    <div style={styles.container}>
      {likelyCauses.length > 0 && (
        <HeaderItem title="Likely Causes">
          <ul style={styles.list}>
            {likelyCauses.map((cause) => (
              <li key={`${cause.sequence}-${cause.message}`}>
                {cause.message}
              </li>
            ))}
          </ul>
        </HeaderItem>
      )}
      {actions.length > 0 && (
        <HeaderItem title="Actions">
          <ul style={styles.list}>
            {actions.map((action) => (
              <li key={`${action.sequence}-${action.message}`}>
                {action.message}
              </li>
            ))}
          </ul>
        </HeaderItem>
      )}
    </div>
  );
};

export default TechnicianActionsPanel;
